// Operator console pages beyond the companies table and the gate (agency users only; anyone else
// gets the not-found page and no API call, as the API answers them 404 or 403):
// - `/operator/consumers`: failed event-consumer records, retry and skip (audit rows 86-88);
// - `/operator/des?p=`: every DE across the granted companies (audit row 84);
// - `/operator/connections`: the platform's service endpoints (CONNECTIONS; platform admins,
//   other agency users see the API's 403 as a notice);
// - `/operator/guards`: the guard rules over DE telemetry (MONITORING-P1; platform admins, the
//   same 403 notice for others).
import { createRoute, useNavigate } from "@tanstack/react-router";
import { isOperator } from "../features/company";
import { AgentHealthHub } from "../features/operator/agent-health";
import { ConnectionsPage } from "../features/operator/connections-page";
import { ConsumersPage } from "../features/operator/consumers-page";
import { GuardsPage } from "../features/operator/guards-page";
import { MemoryQualityPage } from "../features/operator/memory-quality-page";
import { OperatorDes } from "../features/operator/operator-des";
import { SatellitesPage } from "../features/operator/satellites-page";
import {
  OperatorSkillProposalPage,
  OperatorSkillProposalsPage,
} from "../features/operator/skill-proposals-page";
import {
  SkillsmithClientPage,
  SkillsmithClientsPage,
} from "../features/operator/skillsmith-console";
import { SystemAgentSpace } from "../features/operator/system-agent-space";
import { SystemAgentsPage } from "../features/operator/system-agents-page";
import { WorkspaceClientsPage } from "../features/operator/workspace-clients-page";
import { useSession } from "../features/use-feature";
import { validateFocusSearch } from "../focus/focus";
import { currentWeek } from "../focus/weeks";
import { NotFoundPage } from "../pages/status-pages";
import { OperatorShell, PlainShell } from "../shell/app-shell";
import { authedRoute } from "./_authed";

export const operatorConsumersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/consumers",
  component: function ConsumersScreen() {
    const { api } = operatorConsumersRoute.useRouteContext();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <ConsumersPage api={api} me={me} />
      </OperatorShell>
    );
  },
});

export const operatorConnectionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/connections",
  component: function ConnectionsScreen() {
    const { api } = operatorConnectionsRoute.useRouteContext();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <ConnectionsPage api={api} />
      </OperatorShell>
    );
  },
});

/** SATELLITE-REGISTRY: `/operator/satellites` — hosts and the agents registered on them. */
export const operatorSatellitesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/satellites",
  component: function SatellitesScreen() {
    const { api } = operatorSatellitesRoute.useRouteContext();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <SatellitesPage api={api} me={me} />
      </OperatorShell>
    );
  },
});

/** MONITORING-P1: `/operator/guards` — the guard rules over DE telemetry (platform admins). */
export const operatorGuardsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/guards",
  component: function GuardsScreen() {
    const { api } = operatorGuardsRoute.useRouteContext();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <GuardsPage api={api} me={me} />
      </OperatorShell>
    );
  },
});

/** SUPER-ADMIN: `/operator/clients` — the workspace's clients (super admin: all; operator: assigned). */
export const operatorClientsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/clients",
  component: function ClientsScreen() {
    const { api } = operatorClientsRoute.useRouteContext();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <WorkspaceClientsPage api={api} />
      </OperatorShell>
    );
  },
});

/** SKILLSMITH-CONTROL: `/operator/skillsmith` — pick a client to open its Skillsmith (super admins). */
export const operatorSkillsmithRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/skillsmith",
  component: function SkillsmithScreen() {
    const { api } = operatorSkillsmithRoute.useRouteContext();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <SkillsmithClientsPage api={api} />
      </OperatorShell>
    );
  },
});

/** SKILLSMITH-CONTROL: one client's Skillsmith — its thread, orders and standing orders. */
export const operatorSkillsmithClientRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/skillsmith/$companyId",
  component: function SkillsmithClientScreen() {
    const { api } = operatorSkillsmithClientRoute.useRouteContext();
    const { companyId } = operatorSkillsmithClientRoute.useParams();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <SkillsmithClientPage api={api} companyId={companyId} />
      </OperatorShell>
    );
  },
});

/** SYSTEM-AGENT: `/operator/system-agents` — the platform's agents per company and their reports. */
export const operatorSystemAgentsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/system-agents",
  component: function SystemAgentsScreen() {
    const { api } = operatorSystemAgentsRoute.useRouteContext();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <SystemAgentsPage api={api} />
      </OperatorShell>
    );
  },
});

/** SYSTEM-AGENT: a super admin's private space with one system agent (the API gates it). */
export const operatorSystemAgentSpaceRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/system-agents/$companyId/$membershipId",
  component: function SystemAgentSpaceScreen() {
    const { api } = operatorSystemAgentSpaceRoute.useRouteContext();
    const { companyId, membershipId } = operatorSystemAgentSpaceRoute.useParams();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <SystemAgentSpace api={api} companyId={companyId} membershipId={membershipId} />
      </OperatorShell>
    );
  },
});

/** SKILLSMITH-HUB: `/operator/agent-health` — the morning view (super admins; the API gates it). */
export const operatorAgentHealthRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/agent-health",
  component: function AgentHealthScreen() {
    const { api } = operatorAgentHealthRoute.useRouteContext();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <AgentHealthHub api={api} />
      </OperatorShell>
    );
  },
});

/**
 * SKILLSMITH-OPPORTUNITIES: `/operator/agent-health/opportunities?company=` — the helper sales
 * pipeline (super admins; the API gates it). A static segment, so it wins over `$companyId`.
 */
export const operatorOpportunitiesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/agent-health/opportunities",
  validateSearch: (search: Record<string, unknown>): { company?: string } =>
    typeof search.company === "string" && search.company ? { company: search.company } : {},
  component: function OpportunitiesScreen() {
    const { api } = operatorOpportunitiesRoute.useRouteContext();
    const { company } = operatorOpportunitiesRoute.useSearch();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <AgentHealthHub
          api={api}
          view="opportunities"
          {...(company ? { companyId: company } : {})}
        />
      </OperatorShell>
    );
  },
});

/** SKILLSMITH-HUB: `/operator/agent-health/{company}` — one company's topic. */
export const operatorAgentHealthTopicRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/agent-health/$companyId",
  component: function AgentHealthTopicScreen() {
    const { api } = operatorAgentHealthTopicRoute.useRouteContext();
    const { companyId } = operatorAgentHealthTopicRoute.useParams();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <AgentHealthHub api={api} companyId={companyId} />
      </OperatorShell>
    );
  },
});

/**
 * SKILL-PROPOSALS-OPERATOR: `/operator/skill-proposals` — the operator-only skill-proposal queue
 * (super admins; the API gates it), and one proposal at `/{company}/{id}`.
 */
export const operatorSkillProposalsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/skill-proposals",
  component: function SkillProposalsScreen() {
    const { api } = operatorSkillProposalsRoute.useRouteContext();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <OperatorSkillProposalsPage api={api} />
      </OperatorShell>
    );
  },
});

export const operatorSkillProposalRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/skill-proposals/$companyId/$proposalId",
  component: function SkillProposalScreen() {
    const { api } = operatorSkillProposalRoute.useRouteContext();
    const { companyId, proposalId } = operatorSkillProposalRoute.useParams();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <OperatorSkillProposalPage api={api} companyId={companyId} proposalId={proposalId} />
      </OperatorShell>
    );
  },
});

/** MEMORY-QUALITY-WATCH: `/operator/memory-quality/{company}` — findings and thresholds. */
export const operatorMemoryQualityRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/memory-quality/$companyId",
  component: function MemoryQualityScreen() {
    const { api } = operatorMemoryQualityRoute.useRouteContext();
    const { companyId } = operatorMemoryQualityRoute.useParams();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <MemoryQualityPage api={api} companyId={companyId} />
      </OperatorShell>
    );
  },
});

export const operatorDesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/des",
  validateSearch: (search: Record<string, unknown>): { p?: string } => {
    const { p } = validateFocusSearch(search);
    return p ? { p } : {};
  },
  component: function OperatorDesScreen() {
    const { api } = operatorDesRoute.useRouteContext();
    const { p } = operatorDesRoute.useSearch();
    const navigate = useNavigate();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <OperatorDes
          api={api}
          me={me}
          week={p ?? currentWeek()}
          onWeek={(week) => void navigate({ to: "/operator/des", search: { p: week } })}
        />
      </OperatorShell>
    );
  },
});
